// ボスHUD「月光の銀線細工」(2026-10-04 ユーザー裁定)。全ボス共通。
//   枠・名札・紋章 = 画像生成の部品 (public/ui/boss-hud/v2/・切り出し = scripts/boss_hud_parts.py)。
//   帯の中身・満タンの光・揺れ = ここでコードで描く。
//   裁定の原文 (2026-10-04):
//     - 見た目は D「月光の銀線細工」。「体幹をメインだとわかるように、まあ大きくというか太くというか」
//     - 名前の上の飾りは「各ボスのモチーフになりうる…飾り」「ボスによって変える」→ 形だけ (b)。
//       戴冠獣 =「ウ」鎖で玉座に繋がれた王冠 / 介錯 =「イ」布を巻いた剣 / 他7体は見本のまま。hollow-actor (千日手の軍配者たち) は保留。
//     - 満タン (崩せる状態) の色は「赤い色がいい」= Lies of P の組み方 (中身を深い朱へ・白い縁が光る)。
//       ⚠️ 作品の色の決まり「赤＝弾けない攻撃の専用色」の例外 (ユーザー裁定)。自分の体幹の 90% 超の朱と同じ意味 (あと一撃で崩れる)。
//     - 普通のパリィを満タンで当てた時は「足りない」と鈍く揺れる (文字で説明しない・任天堂流)。
//   画像が読めていない間 (初回の読み込み中・テスト) は false を返し、呼び出し側が旧 HUD を描く。

import { COMBAT } from './balance'
import { postureHoldLook, postureHoldTip } from './player_combo3_power'
import { UI_SERIF } from './ui_font'
import { bossHpThickness } from './readability_1104'

const BASE = '/ui/boss-hud/v2/'
const RENDER_SCALE = 2 // 内部の解像度の既定 (shadowBlur は変換を受けないので掛ける)。 2 以外の時の差は render_scale.ts の compensateShadowBlur が埋める

// public/ui/boss-hud/v2/layout.json の値 (画像ピクセル = 論理の2倍)。テストで一致を確かめる。
export const SILVER_HUD_ART = {
  posture_frame: { w: 1021, h: 87, groove: [70.0, 28.1, 880.0, 28.7] },
  hp_frame: { w: 945, h: 46, groove: [37.5, 16.9, 868.8, 11.2] },
  name_plate: { w: 852, h: 71, cuts: [4, 16, 268, 311, 399, 453, 541, 584, 835, 849], panel: [293.5, 21.2, 264.7, 40.0] },
} as const

export const SILVER_HUD_CREST_IDS = [
  'nameless-shade', 'dream-hound', 'vigil-knight', 'bride', 'the-executioner',
  'crowned-king-awoken', 'inverted-jester', 'blade-dancer', 'former-princess',
] as const

// 帯の横幅の比較版 (2026-10-12・DEV ?bossHud=wide60|wide70|wide80・読むのは game.ts の DEV の箱の中だけ)。
//   ユーザー (検収パック 10-12 D-2 のメモ)「もっとボスのUIを横に広く取れば体幹が溜まっている様子やHPが削れている様子が視覚的にわかりやすいんじゃないか」。
//   'now' = 配布版 (体幹の溝 440px = 画面の 45.8%)。wideNN = 体幹の溝を画面幅の NN% にする。太さ・高さ・名札・紋章は変えない。
//   体力の溝は今と同じ比 (体幹の 0.908 倍) で一緒に伸ばす。枠の画像は両端の飾りを伸ばさず、中の真っすぐな所だけを伸ばす (drawFrame)。
//   記録 = docs/research/2026-10-12-boss-hud-wide.md。
export type BossHudWidth = 'now' | 'wide60' | 'wide70' | 'wide80'
export const BOSS_HUD_WIDTH_FRAC: Record<Exclude<BossHudWidth, 'now'>, number> = { wide60: 0.6, wide70: 0.7, wide80: 0.8 }
let hudWidth: BossHudWidth = 'now'
let hudLegacy = false
/** game.ts の DEV の箱の中から ?bossHud= の値を渡す。legacy = 旧 HUD (10-04 より前) との見比べ。知らない値・null = 今のまま。 */
export function setBossHudMode(v: string | null): void {
  hudLegacy = v === 'legacy'
  hudWidth = v === 'wide60' || v === 'wide70' || v === 'wide80' ? v : 'now'
}
export function bossHudWidthMode(): BossHudWidth { return hudWidth }
export function bossHudLegacyMode(): boolean { return hudLegacy }

// 論理座標 (960x540)。下から 体力 → 体幹 → 名札 → 紋章。紋章の月の上端が床 (y440) より下に収まる。
const HP_DRAW_SCALE = 0.92 // 体力の枠を体幹の溝より内側に収める (両端の飾りが体幹の下の棘と重ならない)
export function silverHudLayout(arenaW = 960, width: BossHudWidth = hudWidth) {
  const cx = arenaW / 2
  const P = SILVER_HUD_ART.posture_frame, H = SILVER_HUD_ART.hp_frame, N = SILVER_HUD_ART.name_plate
  const pgH = P.groove[3] / 2
  const nativeW = P.groove[2] / 2
  const pgW = width === 'now' ? nativeW : arenaW * BOSS_HUD_WIDTH_FRAC[width]
  // 体力を太くする比較版 (DEV ?bossHp=a|b・src/readability_1104.ts)。溝と枠を縦に k 倍し、枠の両端の飾りの下端 (溝の上から 10.17k) を
  //   画面の下 538 に収める。体幹・名札・紋章は、体力の枠と重ならないよう少し (a 3px・b 6px) 上へ寄せる。k = 1 は配布版のまま。
  const k = bossHpThickness()
  const dy = Math.round((k - 1) * 6)
  const posture = { x: cx - pgW / 2, y: 505 - dy, w: pgW, h: pgH }
  const hs = HP_DRAW_SCALE / 2
  const hgW = H.groove[2] * hs * (pgW / nativeW)
  const hp = { x: cx - hgW / 2, y: k === 1 ? 526 : 538 - 10.17 * k, w: hgW, h: H.groove[3] * hs * k }
  const panelH = N.panel[3] / 2
  const panelY = 479 - dy
  const plateTop = panelY - N.panel[1] / 2
  const crestH = 28
  const crestBottom = plateTop + 6 // 名札の中央の峰に少し差し込んで立たせる (峰の針先 ≈ plateTop+2.5・峰の本体 ≈ +7)
  return {
    cx, posture, hp, panelY, panelH, plateTop,
    nameBaseline: panelY + panelH / 2 + 6,
    crest: { h: crestH, bottom: crestBottom },
    crescent: { h: 7, bottom: crestBottom - crestH - 1 },
    ruleReach: 200, // 名札の左右の罫の外端 (中心からの距離)
  }
}

type Img = { img: HTMLImageElement; ok: boolean }
const cache: Record<string, Img> = {}
function img(name: string): Img | null {
  if (typeof Image === 'undefined') return null
  let e = cache[name]
  if (!e) {
    const im = new Image()
    e = { img: im, ok: false }
    cache[name] = e
    im.onload = () => { e!.ok = true }
    im.src = BASE + name
  }
  return e
}

export interface SilverHudInput {
  bossId: string
  name: string
  postureRatio: number
  // 赤 (崩せる) に変わる体幹の割合。省略時 1 = 満タンの時だけ赤 (見比べページ boss-hud-lab.html 用)。
  //   ゲームでは breakReadyRatio() =「次のジャストパリィ1回で崩れる」所 (2026-10-05 ユーザー「崩せる所から赤」)。
  breakReadyRatio?: number
  staggered: boolean
  omen: number            // crackOmenIntensity (90% 超の呼吸)
  flash: number           // パリィの報酬の光 (0..1・エスカレーション込み)
  parryAgeSec: number     // 直近の弾きからの経過 (無ければ大きな値)
  parryWasJust: boolean
  hpFraction: number
  hpColors?: readonly string[]  // 見比べ用 (boss-hud-lab.html)。省略時は HP_ENJI
  actMarksW: number
  drawActMarks: (x: number, baseY: number) => void
  // 連撃3段目の得 (2026-10-12・src/heavy_combo_hud.ts)。省略 = 何も描かない。
  //   postureHold = 体幹の戻りが止まっている度合い (1→0)。溜まった所を冷たい銀の霜で覆い、先端に留め金を立てる (drawPostureFrost)。残りで薄れる。
  //     配布版の既定 (DEV ?combo3rd=off で止めない = 描かない・?postureHoldLook=gold で前の金の縁の光)。
  //   heavyGhost = 3段目で削った分 (体力は白く残してから縮む・体幹は溜まった分を白く光らせて金へ戻す)。DEV &combo3rdBar=1 の時だけ。
  postureHold?: number
  heavyGhost?: { hpFrom: number; hpTo: number; postureFrom: number; postureTo: number; hold: number; drain: number } | null
}

// 「次のジャストパリィ1回で崩れる」体幹の割合 (2026-10-05 ユーザー裁定「崩せる所から赤」・調査 H1)。
//   以前の赤は「体幹 = 最大」の間だけで、ボスの体幹は 0.25 秒の猶予の後に減り始めるため 0.25〜0.38 秒で消えていた
//   (docs/research/2026-10-05-shade-practice-stagger.md §3)。表示だけの変更。崩れの判定 (hits.ts) は変えない。
//   ジャストの量 = COMBAT.parrySuccess.bossPostureGain × 最終幕の倍率 (連の倍率・連続弾きの段は 1 として数える=最小の量)。
//   ⚠️ hits.ts では「足した後に満タン以上」で崩れる。足す量の式を変えたらここも合わせる。
//   hpGainMul = 幕の体力による弾きの倍率 (combat.ts parryGainHpMul・10-08 戴冠獣の比較の段 c だけ 1 以外)。
export function breakReadyRatio(b: {
  maxPosture: number; executesTaken: number; roomCount: number; finalRoomPostureMult?: number
}, hpGainMul = 1): number {
  if (b.maxPosture <= 0) return 1
  const finalRoom = b.executesTaken >= Math.max(1, b.roomCount) - 1
  const gain = COMBAT.parrySuccess.bossPostureGain * (finalRoom ? (b.finalRoomPostureMult ?? 1) : 1) * hpGainMul
  return Math.max(0, Math.min(1, 1 - gain / b.maxPosture))
}

// 満タン (=崩せる) になった時刻 (描画だけの状態・ゲームの判定には使わない)
let fullSinceMs = -1
// 体力の削れ跡 (白く残してから追いつく・ソウル系の手応え)。描画だけの状態。
const CHIP_HOLD_MS = 450
const CHIP_CATCHUP_SEC = 0.16
let chipVal = -1, chipLastHp = -1, chipHoldUntil = 0, chipLastMs = -1
/** Separate temporal state for a preview; the normal HUD globals are restored even on failure. */
export function createBossHudPreviewState() {
  return { fullSinceMs: -1, chipVal, chipLastHp, chipHoldUntil, chipLastMs }
}
export function withBossHudPreview<T>(preview: ReturnType<typeof createBossHudPreviewState>, draw: () => T): T {
  const live = { fullSinceMs, chipVal, chipLastHp, chipHoldUntil, chipLastMs }
  ;({ fullSinceMs, chipVal, chipLastHp, chipHoldUntil, chipLastMs } = preview)
  try { return draw() } finally {
    Object.assign(preview, { fullSinceMs, chipVal, chipLastHp, chipHoldUntil, chipLastMs })
    ;({ fullSinceMs, chipVal, chipLastHp, chipHoldUntil, chipLastMs } = live)
  }
}
export function updateHpChip(hp: number, nowMs: number): number {
  const dt = chipLastMs < 0 ? 0 : Math.max(0, Math.min(0.1, (nowMs - chipLastMs) / 1000))
  chipLastMs = nowMs
  if (chipVal < 0 || hp > chipVal) { chipVal = hp; chipLastHp = hp; return chipVal }
  if (hp < chipLastHp - 1e-6) chipHoldUntil = nowMs + CHIP_HOLD_MS // 連続で削られている間は待つ
  chipLastHp = hp
  if (nowMs >= chipHoldUntil && chipVal > hp) {
    chipVal += (hp - chipVal) * Math.min(1, dt / CHIP_CATCHUP_SEC)
    if (chipVal - hp < 0.002) chipVal = hp
  }
  return chipVal
}

const GOLD = ['#fbe9a6', '#e2bb52', '#a87c2a', '#4e3610']
const VERM = ['#ff9a78', '#d84a30', '#962818', '#360c06']
// 体力 = 臙脂 (2026-10-05 ユーザー「臙脂1で、白く残す動きも入れて」)。
//   元の銀は「水色っぽい感じなのが違和感」→ 他作品の整理 (死にゲーの体力は血の赤) → 明るめの臙脂。
//   満タンの体幹 (橙寄りの明るい朱・白く光る) とは、青み寄りの深い赤・光らない・細い で分ける。
const HP_ENJI = ['#e0807a', '#a8343a', '#6e1a22', '#2e080c']

function mix(a: string, b: string, k: number): string {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16))
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16))
  return '#' + pa.map((v, i) => Math.round(v + (pb[i] - v) * k).toString(16).padStart(2, '0')).join('')
}

// 金箔の筋 (overlay で重ねる)。1回だけ作る。
let leaf: HTMLCanvasElement | null = null
function leafTex(): HTMLCanvasElement | null {
  if (leaf || typeof document === 'undefined') return leaf
  leaf = document.createElement('canvas'); leaf.width = 512; leaf.height = 32
  const g = leaf.getContext('2d')
  if (!g) return leaf
  let seed = 7
  const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647
  for (let i = 0; i < 260; i++) {
    const y = rnd() * 32, x = rnd() * 512, w = 20 + rnd() * 140, a = 0.05 + rnd() * 0.16
    g.fillStyle = rnd() < 0.5 ? `rgba(255,250,225,${a})` : `rgba(60,36,8,${a})`
    g.fillRect(x, y, w, 0.6 + rnd() * 1.2)
  }
  return leaf
}

function fillGroove(
  ctx: CanvasRenderingContext2D, r: { x: number; y: number; w: number; h: number },
  ratio: number, cols: string[], leafA: number, tipGlow: boolean,
): void {
  if (ratio <= 0) return
  const fw = r.w * Math.min(1, ratio)
  ctx.save()
  ctx.beginPath(); ctx.rect(r.x, r.y, fw, r.h); ctx.clip()
  const g = ctx.createLinearGradient(0, r.y, 0, r.y + r.h)
  g.addColorStop(0, cols[0]); g.addColorStop(0.32, cols[1]); g.addColorStop(0.72, cols[2]); g.addColorStop(1, cols[3])
  ctx.fillStyle = g; ctx.fillRect(r.x, r.y, fw, r.h)
  const tex = leafTex()
  if (tex && leafA > 0) {
    ctx.globalAlpha = leafA; ctx.globalCompositeOperation = 'overlay'
    const tw = 512 * (r.h / 32) * 2.2
    for (let x = r.x; x < r.x + fw; x += tw) ctx.drawImage(tex, 0, 0, 512, 32, x, r.y, tw, r.h)
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over'
  }
  ctx.fillStyle = 'rgba(255,248,220,0.45)'; ctx.fillRect(r.x, r.y + 0.6, fw, 0.9)
  ctx.restore()
  if (tipGlow && ratio < 1) {
    const tx = r.x + fw, cy = r.y + r.h / 2
    ctx.save()
    ctx.beginPath(); ctx.rect(r.x, r.y, r.w, r.h); ctx.clip()
    ctx.globalCompositeOperation = 'lighter'
    const rg = ctx.createRadialGradient(tx, cy, 0, tx, cy, r.h * 1.6)
    rg.addColorStop(0, 'rgba(255,236,170,0.55)'); rg.addColorStop(1, 'rgba(255,236,170,0)')
    ctx.fillStyle = rg; ctx.fillRect(tx - r.h * 2, r.y, r.h * 2, r.h)
    ctx.fillStyle = 'rgba(255,250,230,0.85)'; ctx.fillRect(tx - 0.8, r.y, 0.8, r.h)
    ctx.restore()
  }
}

// 横幅の比較版で枠を伸ばす時に、伸ばさずに残す両端の長さ (画像ピクセル・溝の端から内側へ)。飾りと溝の角を含む。
const FRAME_CAP_IN = 40
function drawFrame(ctx: CanvasRenderingContext2D, e: Img, art: { w: number; h: number; groove: readonly number[] }, r: { x: number; y: number; w: number; h: number }): void {
  // 溝の位置から枠全体の置き場所を逆算する (art は画像ピクセル)
  const s = r.h / art.groove[3]
  const sw = r.w / art.groove[2]
  if (Math.abs(sw - s) < 1e-6) {
    ctx.drawImage(e.img, r.x - art.groove[0] * s, r.y - art.groove[1] * s, art.w * s, art.h * s)
    return
  }
  // 横幅の比較版 (DEV): 両端 (飾り) は今の大きさのまま、間の真っすぐな所だけを横へ伸ばす。
  const y = r.y - art.groove[1] * s, h = art.h * s
  const lEnd = art.groove[0] + FRAME_CAP_IN               // 左の飾りの終わり (画像)
  const rBeg = art.groove[0] + art.groove[2] - FRAME_CAP_IN // 右の飾りの始まり (画像)
  const dx0 = r.x - art.groove[0] * s
  const dl = r.x + FRAME_CAP_IN * s
  const dr = r.x + r.w - FRAME_CAP_IN * s
  ctx.drawImage(e.img, 0, 0, lEnd, art.h, dx0, y, lEnd * s, h)
  ctx.drawImage(e.img, lEnd, 0, rBeg - lEnd, art.h, dl - 0.25, y, dr - dl + 0.5, h) // 0.25px 重ねて継ぎ目を消す
  ctx.drawImage(e.img, rBeg, 0, art.w - rBeg, art.h, dr, y, (art.w - rBeg) * s, h)
}

function drawPlate(ctx: CanvasRenderingContext2D, e: Img, L: ReturnType<typeof silverHudLayout>, panelW: number): void {
  const N = SILVER_HUD_ART.name_plate, c = N.cuts, k = 0.5
  const y = L.plateTop, h = N.h * k
  const seg = (sx0: number, sx1: number, dx: number, dw: number) => {
    if (dw <= 0.01) return
    ctx.drawImage(e.img, sx0, 0, sx1 - sx0, N.h, dx, y, dw, h)
  }
  const pl = L.cx - panelW / 2, pr = L.cx + panelW / 2
  const panelL0 = N.panel[0], panelR1 = N.panel[0] + N.panel[2]
  // 左: 菱 → 罫 (伸ばす) → 渦飾り (名札の地を少し含む)
  const flL = pl - (panelL0 - c[2]) * k, flLw = (c[3] - c[2]) * k
  const dL = L.cx - L.ruleReach
  seg(c[0], c[1], dL, (c[1] - c[0]) * k)
  seg(c[1] + 20, c[1] + 24, dL + (c[1] - c[0]) * k, flL - (dL + (c[1] - c[0]) * k))
  seg(c[2], c[3], flL, flLw)
  // 名札の地 (伸ばす) → 中央の峰 → 名札の地 (伸ばす)
  const peakW = (c[5] - c[4]) * k
  seg(c[3] + 30, c[3] + 34, flL + flLw, L.cx - peakW / 2 - (flL + flLw))
  seg(c[4], c[5], L.cx - peakW / 2, peakW)
  const flR = pr - (panelR1 - c[6]) * k, flRw = (c[7] - c[6]) * k
  seg(c[5] + 20, c[5] + 24, L.cx + peakW / 2, flR - (L.cx + peakW / 2))
  // 右: 渦飾り → 罫 (伸ばす) → 菱
  seg(c[6], c[7], flR, flRw)
  const dRw = (c[9] - c[8]) * k, dR = L.cx + L.ruleReach - dRw
  seg(c[7] + 20, c[7] + 24, flR + flRw, dR - (flR + flRw))
  seg(c[8], c[9], dR, dRw)
}

function glowDot(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, a: number): void {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r)
  g.addColorStop(0, `rgba(255,236,160,${a})`); g.addColorStop(1, 'rgba(168,138,40,0)')
  ctx.fillStyle = g; ctx.fillRect(x - r, y - r, r * 2, r * 2)
}

function hash01(n: number): number {
  const s = Math.sin(n * 127.1) * 43758.5453
  return s - Math.floor(s)
}

/** 枠・名札・紋章を先に読む (11-04・遅い回線で一戦の頭に帯の枠が数秒出ない「読み込みの未着」を防ぐ・main.ts がボスの替わり目に呼ぶ)。 */
export function preloadBossHud(bossId: string): void {
  img('posture_frame.png'); img('hp_frame.png'); img('name_plate.png'); img('crests/crescent.png')
  if ((SILVER_HUD_CREST_IDS as readonly string[]).includes(bossId)) img(`crests/${bossId}.png`)
}

export function drawBossHudSilver(ctx: CanvasRenderingContext2D, s: SilverHudInput, nowMs = performance.now()): boolean {
  const pf = img('posture_frame.png'), hf = img('hp_frame.png'), np = img('name_plate.png')
  const crestId = (SILVER_HUD_CREST_IDS as readonly string[]).includes(s.bossId) ? s.bossId : null
  const cr = crestId ? img(`crests/${crestId}.png`) : null
  const moon = img('crests/crescent.png')
  if (!pf?.ok || !hf?.ok || !np?.ok) return false
  const L = silverHudLayout()
  const t = nowMs / 1000
  const ratio = Math.max(0, Math.min(1, s.postureRatio))
  const full = ratio >= Math.min(1, s.breakReadyRatio ?? 1) - 1e-9 && ratio > 0 && !s.staggered
  if (full) { if (fullSinceMs < 0) fullSinceMs = nowMs } else fullSinceMs = -1
  const since = full ? nowMs - fullSinceMs : -1
  // 満タンのまま普通のパリィが当たった = 「足りない」の鈍い揺れ (ジャストは崩れるので staggered 側へ行く)
  const parryAtMs = nowMs - s.parryAgeSec * 1000
  const hitT = full && !s.parryWasJust && s.parryAgeSec < 0.32 && parryAtMs > fullSinceMs + 60 ? s.parryAgeSec * 1000 : -1
  const hit = hitT >= 0 ? 1 - hitT / 320 : 0

  ctx.save()
  // 下端の暗がり (文字と帯を床から浮かせる)
  {
    const top = 436
    const g = ctx.createLinearGradient(0, top, 0, top + 44)
    g.addColorStop(0, 'rgba(8,5,12,0)'); g.addColorStop(1, 'rgba(8,5,12,0.52)')
    ctx.fillStyle = g; ctx.fillRect(0, top, 960, 540 - top)
  }

  // ===== 名札・紋章 =====
  ctx.font = `600 17px ${UI_SERIF}`
  const nameW = ctx.measureText(s.name).width
  const padX = 14, markGap = s.actMarksW > 0 ? 12 : 0
  const panelW = Math.max(120, padX * 2 + nameW + markGap + s.actMarksW)
  drawPlate(ctx, np, L, panelW)
  if (cr?.ok) {
    const h = L.crest.h, w = cr.img.naturalWidth * (h / cr.img.naturalHeight)
    ctx.drawImage(cr.img, L.cx - w / 2, L.crest.bottom - h, w, h)
  }
  if (moon?.ok) {
    const h = L.crescent.h, w = moon.img.naturalWidth * (h / moon.img.naturalHeight)
    ctx.drawImage(moon.img, L.cx - w / 2, L.crescent.bottom - h, w, h)
  }
  const nameX = L.cx - panelW / 2 + padX + (panelW - (padX * 2 + nameW + markGap + s.actMarksW)) / 2
  ctx.textAlign = 'left'
  ctx.strokeStyle = 'rgba(6,4,10,0.88)'; ctx.lineWidth = 3
  ctx.strokeText(s.name, nameX, L.nameBaseline)
  ctx.fillStyle = '#ede0c4'; ctx.shadowColor = 'rgba(148,120,68,0.48)'; ctx.shadowBlur = 9
  ctx.fillText(s.name, nameX, L.nameBaseline)
  ctx.shadowBlur = 0
  s.drawActMarks(nameX + nameW + markGap, L.nameBaseline)

  // ===== 体幹 =====
  const P = L.posture
  ctx.save()
  if (hit > 0) ctx.translate(Math.sin(hitT / 18) * 3 * hit, 0)
  drawFrame(ctx, pf, SILVER_HUD_ART.posture_frame, P)
  const k = full ? Math.min(1, since / 200) : 0
  const cols = GOLD.map((g, i) => mix(g, VERM[i], k))
  fillGroove(ctx, P, ratio, cols, 0.9, !s.staggered)
  if (ratio > 0) {
    const fw = P.w * ratio
    ctx.save()
    ctx.beginPath(); ctx.rect(P.x, P.y, fw, P.h); ctx.clip()
    ctx.globalCompositeOperation = 'lighter'
    if (s.staggered) {
      // 崩れている間: 金白の脈動 (旧 HUD と同じ)
      const pulse = 0.5 + 0.5 * Math.sin(t * 6)
      ctx.globalAlpha = 0.3 + pulse * 0.32
      ctx.fillStyle = 'rgba(255,245,180,1)'; ctx.fillRect(P.x, P.y, fw, P.h)
    } else {
      if (s.omen > 0 && k < 1) {
        // 90% 超: 金が呼吸する (満タンで赤に変わると止める)
        const beat = Math.max(Math.sin(t * 1.6) * 0.5 + 0.5, Math.max(0, Math.sin(t * 1.6 + 0.4)) * 0.85)
        ctx.globalAlpha = s.omen * (0.45 + beat * 0.55) * 0.45 * (1 - k)
        ctx.fillStyle = '#fff2c8'; ctx.fillRect(P.x, P.y, fw, P.h)
      }
      if (s.flash > 0 && !full) {
        ctx.globalAlpha = Math.min(1, s.flash) * 0.42
        ctx.fillStyle = '#ffffff'; ctx.fillRect(P.x, P.y, fw, P.h)
      }
    }
    ctx.restore()
  }
  // 連撃3段目の比較版 (DEV だけ・省略 = 何も描かない)。満タン (赤) と崩れの間は、そちらの光を優先して描かない。
  const hg = s.heavyGhost
  if (hg && !s.staggered && !full) {
    // 案2 体幹: 3段目で溜まった分を白く光らせ、一拍の後に金へ戻す (伸びそのものは今どおりすぐ)。
    const x0 = P.x + P.w * Math.min(hg.postureFrom, ratio)
    const x1 = P.x + P.w * Math.min(hg.postureTo, ratio)
    const a = 1 - hg.drain
    if (x1 - x0 > 0.3 && a > 0) {
      ctx.save()
      ctx.beginPath(); ctx.rect(P.x, P.y, P.w * ratio, P.h); ctx.clip()
      ctx.globalCompositeOperation = 'lighter'
      ctx.fillStyle = `rgba(255,250,232,${(0.78 * a).toFixed(3)})`
      ctx.fillRect(x0, P.y, x1 - x0, P.h)
      ctx.restore()
    }
  }
  const holdLv = s.postureHold ?? 0
  if (holdLv > 0 && !s.staggered && !full && postureHoldLook() === 'frost') {
    drawPostureFrost(ctx, P, ratio, holdLv, t)
  } else if (holdLv > 0 && !s.staggered && !full) {
    // 前の形 (DEV ?postureHoldLook=gold): 戻りが止まっている間、帯の縁が真鍮・金でほのかに光る。残りの時間で光が細り、解けると消える。
    //   紫 (致命・パリィで返す攻撃) と赤 (弾けない攻撃) は使わない。満タンの白い縁より弱く・細く。
    //   加算で重ねると銀の枠と混ざって白っぽくなり、満タンの白い縁と紛れる (10-12 の撮影で確かめた)。
    //   縁の線は普通に塗って金に染め、にじみだけを加算で足す。
    const a = 0.15 + 0.6 * holdLv
    ctx.save()
    ctx.lineWidth = 0.8 + 0.8 * holdLv
    ctx.shadowColor = `rgba(214,158,52,${(0.9 * a).toFixed(3)})`
    ctx.shadowBlur = 8 * (0.35 + 0.65 * holdLv) * RENDER_SCALE
    ctx.strokeStyle = `rgba(226,178,78,${Math.min(1, 0.95 * a).toFixed(3)})`
    ctx.strokeRect(P.x - 1, P.y - 1, P.w + 2, P.h + 2)
    ctx.globalCompositeOperation = 'lighter'
    ctx.shadowBlur = 14 * (0.35 + 0.65 * holdLv) * RENDER_SCALE
    ctx.shadowColor = `rgba(200,140,40,${(0.5 * a).toFixed(3)})`
    ctx.strokeStyle = `rgba(160,110,30,${(0.35 * a).toFixed(3)})`
    ctx.strokeRect(P.x - 1.5, P.y - 1.5, P.w + 3, P.h + 3)
    ctx.restore()
    // 稽古の間だけの真鍮の印と斜めの刻み (止めていなければ減っていた分) は、10-12 の答え E-2「要らない」で外した。
    //   戻すなら git の 66cef4d6 の drawBossPostureGauge と heavy_combo_hud.ts postureHoldSavedRatio を見る。
  }
  if (full) {
    // 満タン: 白い縁が光る。満ちた瞬間に一度強く、あとは弱く残る。揺れの間は光が引く。
    const burst = Math.max(0, 1 - since / 450)
    let edge = 0.75 + 0.12 * Math.sin(t * 3) + burst * 0.9
    if (hit > 0) edge *= 1 - 0.75 * Math.min(1, hitT / 60) * hit
    const grow = burst * 4
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    ctx.lineWidth = 1.2
    for (const [blur, kk] of [[18, 0.75], [8, 0.85], [3, 0.6]] as const) {
      ctx.shadowColor = `rgba(255,255,255,${Math.min(1, kk * edge)})`
      ctx.shadowBlur = (blur + grow * 2) * RENDER_SCALE
      ctx.strokeStyle = `rgba(255,255,255,${Math.min(1, 0.65 * edge)})`
      ctx.strokeRect(P.x - 1.5 - grow * 0.6, P.y - 1.5 - grow * 0.25, P.w + 3 + grow * 1.2, P.h + 3 + grow * 0.5)
    }
    ctx.restore()
  }
  if (hit > 0) {
    ctx.save(); ctx.globalAlpha = 0.55 * hit; ctx.fillStyle = '#2a2230'; ctx.fillRect(P.x, P.y, P.w, P.h); ctx.restore()
  }
  ctx.restore()

  // 金の粒が帯の先端で弾ける (弾いた報酬・満タン前だけ)
  if (s.flash > 0 && ratio > 0 && !full && s.parryAgeSec < 0.35) {
    const tipX = P.x + P.w * ratio, tipY = P.y + P.h / 2
    const prog = s.parryAgeSec / 0.35
    ctx.save(); ctx.globalCompositeOperation = 'lighter'
    const n = 6 + Math.round(3 * Math.min(2, s.flash))
    for (let i = 0; i < n; i += 1) {
      const ang = -Math.PI / 2 + (hash01(i * 1.7) - 0.5) * 2.1
      const sp = 16 + hash01(i * 2.3) * 28
      glowDot(ctx, tipX + Math.cos(ang) * sp * prog, tipY + Math.sin(ang) * sp * prog - 5 * prog,
        2.2 + 2.4 * hash01(i * 3.1), (1 - prog) * (0.55 + 0.45 * hash01(i * 1.1)))
    }
    ctx.restore()
  }

  // ===== 体力 (今の幕の残り・銀) =====
  drawFrame(ctx, hf, SILVER_HUD_ART.hp_frame, L.hp)
  const hpNow = Math.max(0, Math.min(1, s.hpFraction))
  const chip = updateHpChip(hpNow, nowMs)
  fillGroove(ctx, L.hp, hpNow, s.hpColors ? [...s.hpColors] : HP_ENJI, 0.35, false)
  if (chip > hpNow) {
    const H = L.hp, x0 = H.x + H.w * hpNow, w = H.w * (chip - hpNow)
    ctx.save()
    ctx.fillStyle = 'rgba(255,242,232,0.88)'; ctx.fillRect(x0, H.y, w, H.h)
    ctx.restore()
  }
  // 連撃3段目の比較版 案2 体力 (DEV だけ): 3段目で削った分を、普段の削れ跡より明るく・ふちを光らせて一拍残し、それから縮める。
  //   普段の削れ跡 (上の chip・全段) は 450ms で追いつくので、3段目の分だけが一拍遅れて残って見える。
  if (hg) {
    const H = L.hp
    const e = hg.drain * hg.drain
    const right = hg.hpFrom - (hg.hpFrom - hpNow) * e
    if (right > hpNow + 1e-4) {
      const x0 = H.x + H.w * hpNow, w = H.w * (right - hpNow)
      ctx.save()
      ctx.beginPath(); ctx.rect(H.x, H.y - 4, H.w, H.h + 8); ctx.clip()
      ctx.fillStyle = 'rgba(255,253,246,0.97)'
      ctx.shadowColor = `rgba(255,236,200,${(0.75 * (1 - hg.drain)).toFixed(3)})`
      ctx.shadowBlur = 6 * RENDER_SCALE
      ctx.fillRect(x0, H.y, w, H.h)
      ctx.restore()
    }
  }

  ctx.restore()
  return true
}

/**
 * 体幹の戻りが止まっている間の帯 (2026-10-28 作者「色が近すぎるのとうまく囲えていない」)。
 * 金の帯の上を冷たい銀の霜で覆い、霜の上を光がゆっくり流れ、溜まった先端に留め金を立てる = 「ここで止めてある」。
 * 色は金と分ける冷たい銀 (紫 = 致命・赤 = 弾けない攻撃・白 = 満タン の縁とも分ける)。holdLv (1→0) で薄れる。
 */
function drawPostureFrost(ctx: CanvasRenderingContext2D, P: { x: number; y: number; w: number; h: number }, ratio: number, holdLv: number, t: number): void {
  const a = 0.35 + 0.65 * holdLv
  const fw = P.w * Math.max(0, Math.min(1, ratio))
  ctx.save()
  if (fw > 0.5) {
    // 霜: 溜まった所を冷たい銀で覆う (金を沈める)。
    ctx.save()
    ctx.beginPath(); ctx.rect(P.x, P.y, fw, P.h); ctx.clip()
    const g = ctx.createLinearGradient(0, P.y, 0, P.y + P.h)
    g.addColorStop(0, `rgba(226,240,252,${(0.62 * a).toFixed(3)})`)
    g.addColorStop(0.5, `rgba(168,198,226,${(0.5 * a).toFixed(3)})`)
    g.addColorStop(1, `rgba(120,150,184,${(0.58 * a).toFixed(3)})`)
    ctx.fillStyle = g; ctx.fillRect(P.x, P.y, fw, P.h)
    // 霜の上を流れる光 (止めた画面でも壁時計で動く)。
    const span = 70
    const sx = P.x - span + ((t * 120) % (fw + span * 2))
    const sg = ctx.createLinearGradient(sx, 0, sx + span, 0)
    sg.addColorStop(0, 'rgba(255,255,255,0)')
    sg.addColorStop(0.5, `rgba(236,248,255,${(0.55 * a).toFixed(3)})`)
    sg.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.globalCompositeOperation = 'lighter'
    ctx.fillStyle = sg; ctx.fillRect(sx, P.y, span, P.h)
    ctx.restore()
    // 溜まった所の縁取り (氷色)。
    ctx.lineWidth = 1.4
    ctx.shadowColor = `rgba(150,206,255,${(0.9 * a).toFixed(3)})`
    ctx.shadowBlur = 8 * RENDER_SCALE
    ctx.strokeStyle = `rgba(206,232,255,${(0.95 * a).toFixed(3)})`
    ctx.strokeRect(P.x - 1, P.y - 1, fw + 2, P.h + 2)
  }
  const tip = postureHoldTip()
  if (tip !== 'clasp') {
    ctx.shadowBlur = 0
    if (tip === 'glow') drawHoldTipGlow(ctx, P, P.x + fw, a, t)
    else if (tip === 'crystal') drawHoldTipCrystal(ctx, P, fw, a, t)
    else if (tip === 'flame') drawHoldTipFlame(ctx, P, P.x + fw, a, t)
    ctx.restore()
    return
  }
  // 留め金: 溜まった先端に、溝より上下にはみ出す縦の棒と上下の楔。
  const cx = P.x + fw
  const over = 6
  ctx.shadowColor = `rgba(150,206,255,${a.toFixed(3)})`
  ctx.shadowBlur = 10 * RENDER_SCALE
  ctx.fillStyle = `rgba(236,246,255,${Math.min(1, 1.05 * a).toFixed(3)})`
  ctx.fillRect(cx - 1.5, P.y - over, 3, P.h + over * 2)
  ctx.beginPath()
  ctx.moveTo(cx - 5, P.y - over - 4); ctx.lineTo(cx + 5, P.y - over - 4); ctx.lineTo(cx, P.y - over + 1); ctx.closePath()
  ctx.moveTo(cx - 5, P.y + P.h + over + 4); ctx.lineTo(cx + 5, P.y + P.h + over + 4); ctx.lineTo(cx, P.y + P.h + over - 1); ctx.closePath()
  ctx.fill()
  ctx.restore()
}

type HudRect = { x: number; y: number; w: number; h: number }

/** 先端の印 'glow' (灯の滲み): 先端に青白い灯がにじむ。縁の無い光だけで、ゆっくり息をする。 */
function drawHoldTipGlow(ctx: CanvasRenderingContext2D, P: HudRect, cx: number, a: number, t: number): void {
  const cy = P.y + P.h / 2
  const breath = 0.5 + 0.5 * Math.sin(t * 2.2)
  const r = 16 + 3 * breath
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.translate(cx, cy)
  ctx.scale(1, 0.5)
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r)
  g.addColorStop(0, `rgba(244,251,255,${(0.85 * a).toFixed(3)})`)
  g.addColorStop(0.3, `rgba(170,214,255,${((0.45 + 0.15 * breath) * a).toFixed(3)})`)
  g.addColorStop(1, 'rgba(110,170,255,0)')
  ctx.fillStyle = g
  ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.fill()
  ctx.restore()
}

/** 先端の印 'crystal' (霜の結晶): 先端から空の溝へ細い霜の針が伸び、粒が瞬く。溝の外へは出さない。 */
const CRYSTAL_NEEDLES = [
  { y: 0.15, len: 14, tilt: -1.2, w: 1.6 }, { y: 0.36, len: 24, tilt: -0.4, w: 2.4 }, { y: 0.52, len: 10, tilt: 0.3, w: 1.8 },
  { y: 0.68, len: 20, tilt: 0.7, w: 2.2 }, { y: 0.88, len: 12, tilt: 1.4, w: 1.6 },
]
function drawHoldTipCrystal(ctx: CanvasRenderingContext2D, P: HudRect, fw: number, a: number, t: number): void {
  const cx = P.x + fw
  ctx.save()
  ctx.beginPath(); ctx.rect(P.x, P.y, P.w, P.h); ctx.clip()
  ctx.shadowColor = `rgba(150,206,255,${(0.8 * a).toFixed(3)})`
  ctx.shadowBlur = 4 * RENDER_SCALE
  ctx.fillStyle = `rgba(222,240,255,${(0.9 * a).toFixed(3)})`
  CRYSTAL_NEEDLES.forEach((n, i) => {
    const y = P.y + P.h * n.y
    const len = n.len * (0.85 + 0.15 * Math.sin(t * 1.3 + i * 1.7))
    ctx.beginPath()
    ctx.moveTo(cx - 2, y - n.w); ctx.lineTo(cx + len, y + n.tilt * 3); ctx.lineTo(cx - 2, y + n.w)
    ctx.closePath(); ctx.fill()
  })
  // 霜の粒: 先端のまわりで順に瞬く小さな十字。
  ctx.globalCompositeOperation = 'lighter'
  for (let i = 0; i < 3; i++) {
    const tw = Math.max(0, Math.sin(t * 2.6 + i * 2.1))
    if (tw <= 0.05) continue
    const px = cx - 10 + i * 12, py = P.y + P.h * (0.3 + 0.2 * i)
    const s = 4 * tw
    ctx.fillStyle = `rgba(250,253,255,${(0.9 * tw * a).toFixed(3)})`
    ctx.fillRect(px - s, py - 0.5, s * 2, 1)
    ctx.fillRect(px - 0.5, py - s, 1, s * 2)
  }
  ctx.restore()
}

/** 先端の印 'flame' (灯の揺れ): 先端の空の溝に小さな青い灯火が立ち、揺れる。溝の外へは出さない。 */
function drawHoldTipFlame(ctx: CanvasRenderingContext2D, P: HudRect, cx0: number, a: number, t: number): void {
  const cx = cx0 + 7
  const base = P.y + P.h - 1
  const hgt = (P.h - 3) * (0.86 + 0.1 * Math.sin(t * 9) + 0.04 * Math.sin(t * 23))
  const sway = Math.sin(t * 5) * 1.2
  const flame = (w: number, h: number) => {
    ctx.beginPath()
    ctx.moveTo(cx, base + 1)
    ctx.quadraticCurveTo(cx - w, base - h * 0.35, cx + sway, base - h)
    ctx.quadraticCurveTo(cx + w, base - h * 0.35, cx, base + 1)
    ctx.closePath()
  }
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.shadowColor = `rgba(110,170,255,${a.toFixed(3)})`
  ctx.shadowBlur = 10 * RENDER_SCALE
  ctx.fillStyle = `rgba(120,176,255,${(0.75 * a).toFixed(3)})`
  ctx.beginPath(); ctx.rect(P.x, P.y, P.w, P.h); ctx.clip()
  flame(7, hgt); ctx.fill()
  ctx.shadowBlur = 0
  ctx.fillStyle = `rgba(240,248,255,${(0.9 * a).toFixed(3)})`
  flame(3, hgt * 0.55); ctx.fill()
  ctx.restore()
}
